/**
 * The wires CSS cannot run on this page. All of them are here rather than in a
 * section because a section's inline <script> does not execute reliably in this
 * theme (see duet-horizon/BLOCKED.md).
 *
 * 1. COLOUR → GALLERY. The buy panel's colour cards and the gallery live in
 *    two separate radio groups by necessity: one posts a variant id inside
 *    {% form 'product' %}, the other only decides which photograph is showing.
 *    :checked reaches later siblings only, and the gallery's radios sit inside
 *    .gal-col, which is BEFORE .buy-panel in .buy-zone, so no selector can
 *    join them. One way, deliberately — moving along the gallery must never
 *    quietly change what the form will buy.
 *
 *    2026-08-30: this stopped being a frame MAP. The two packshots used to be
 *    slides 1 and 2 and a colour choice jumped between them; Leo wanted the
 *    lamplight couple at slide 2 and the grey pillow hidden until someone
 *    picks grey, which cannot both be true while slide 2 is the grey packshot.
 *    So frame 1 now carries both packshots and the colour toggles .is-grey on
 *    .gal-col — d2-hero's stylesheet does the swap, here and on the desktop
 *    thumbnail. The row is still carried to frame 1, because the picture a
 *    colour choice is about is the one that just changed.
 *
 *    2026-09-22: the picker sells two LIFTS as well as two shades, so frame 1
 *    carries four packshots and .gal-col wears two classes, .is-grey and
 *    .is-std. The radios say which is which in data-col and data-ver rather
 *    than in their ids, so this file no longer has to parse a string to know
 *    what was picked. They also carry every piece of copy the two lifts
 *    differ on — price, struck price, saving, both buttons, the bar and the
 *    included line — already formatted by Liquid; syncCopy copies them across
 *    and does no arithmetic of its own.
 *
 * 2. THE PHONE GALLERY (added 2026-08-23). Below 990 the frames are a
 *    scroll-snap row, not a crossfade stack, so three things need joining that
 *    CSS cannot join: the scroll position has to drive the progress drag, a tap
 *    on a scrollbar segment has to scroll rather than only tick a radio, and a
 *    colour choice has to carry the row to the matching frame. One function,
 *    goToFrame, is the single way any of those move the row.
 *
 *    The drag is set as an INLINE transform. d2-hero's two
 *    `#d2-gal-N:checked ~ .gal-bar .gal-bar__drag` rules carry an id and would
 *    win against any class selector this file could target; inline wins against
 *    both, and leaving those rules live is what keeps a segment tap visible for
 *    anyone running without JavaScript. Both inline properties are cleared
 *    again above 990 so the desktop bar goes back to the stylesheet's steps.
 *
 * 3. THE SCROLL INSET. From 990px up, Horizon hands scrolling to .page-wrapper
 *    (base.css: height 100dvh, overflow-y auto) and puts html/body at
 *    overflow:hidden. So the page's content box is ~11px narrower than the
 *    viewport, while a position:fixed element still measures against the
 *    viewport — which left the sticky buy bar hanging that far past the right
 *    edge of every card above it. The width is the platform's, not a number we
 *    get to pick, so it is measured rather than typed.
 *
 * 4. THE CLOSING BUTTON'S VARIANT (added 2026-08-23). sections/d2-close.liquid
 *    adds to cart nine screens below the picker. It used to add the bundle,
 *    which had one variant and needed no thought; it now adds the pillow, which
 *    has two colours. Rather than post a hard-coded Black at someone who chose
 *    Grey, its hidden input mirrors whatever the picker is on. With this script
 *    absent the markup's own value posts, which is the same Black the picker
 *    itself opens on — the failure mode is the default, never the wrong colour.
 */
(() => {
  const galCol = document.querySelector('.gal-col');

  // ---- the phone gallery ---------------------------------------------------

  // "d2-gal-2" -> 1, "d2-opt-…" -> NaN. One reader for both ids so a fourth
  // frame does not need a second parser.
  const frameIndexOf = (id) => {
    const match = /-(\d+)$/.exec(id || '');
    return match ? Number(match[1]) - 1 : NaN;
  };

  const stack = document.querySelector('.gal-col .gal-stack');
  const frames = stack ? Array.from(stack.querySelectorAll('.gal-frame')) : [];
  const drag = document.querySelector('.gal-col .gal-bar__drag');

  // The row is only a scroller below 990. Asking the element rather than
  // matchMedia'ing a number keeps the breakpoint in one place — the stylesheet
  // — instead of two that can drift apart.
  //
  // TWO tests, and the overflow one is the load-bearing half (2026-08-29). A
  // flex track whose frames are each 100% wide reports scrollWidth ~4x
  // clientWidth at EVERY width, scroller or not — above 990 those frames
  // simply spill out of a static track and .img-card clips them. On the
  // measurement alone this function said "scroller" on the desktop too, so
  // goToFrame pushed scrollLeft on a box that does not scroll and paintDrag
  // read that position back and re-checked the frame radio — the desktop
  // picture switcher was being driven by phone code the whole time.
  // Only a computed overflow-x of auto/scroll is actually a scroll container.
  // `!== 'hidden'` would NOT do: the desktop track's overflow-x is `visible`
  // (that is the fix above), which sails straight through such a test.
  const isScroller = () => {
    if (!stack || frames.length < 2) return false;
    const overflowX = getComputedStyle(stack).overflowX;
    if (overflowX !== 'auto' && overflowX !== 'scroll') return false;
    return stack.scrollWidth - stack.clientWidth > 1;
  };

  const goToFrame = (index, smooth) => {
    const frame = frames[index];
    if (!frame || !isScroller()) return;
    const left = frame.offsetLeft - frames[0].offsetLeft;
    if (typeof stack.scrollTo === 'function') {
      stack.scrollTo({ left, behavior: smooth ? 'smooth' : 'auto' });
    } else {
      stack.scrollLeft = left;
    }
  };

  // The single writer of the colour state, and the only thing that still joins
  // the buy panel to the gallery. Also parks the row on frame 1: the packshot
  // that just changed is the picture the choice was about, and on a phone it
  // may be two swipes away. Defined after goToFrame because it calls it.
  // 2026-09-22: the panel's radios carry data-ver ('pro' | 'std') and data-col
  // ('black' | 'grey') rather than encoding the choice in their id. Frame 1
  // holds four packshots and .gal-col wears the two states as two classes.
  // 2026-09-23: the packshot frame is slide 2 (d2-hero data-pack) and the
  // lead photograph is slide 1. A pick still parks on the packshot; the
  // opening sync passes park=false so a plain visit stays on the lead.
  const packPos = Number(galCol && galCol.dataset.pack) || 1;
  const applyPick = (radio, smooth, park = true) => {
    if (galCol) {
      galCol.classList.toggle('is-grey', radio.dataset.col === 'grey');
      galCol.classList.toggle('is-std', radio.dataset.ver === 'std');
    }
    if (!park) return;
    const packRadio = document.getElementById('d2-gal-' + packPos);
    if (packRadio && !packRadio.checked) packRadio.checked = true;
    goToFrame(packPos - 1, smooth);
  };

  // Everything the two lifts change that is not the picture. Every string is
  // formatted by Liquid off its own variant and parked on the radio, so this
  // function copies and never computes — no money arithmetic in JavaScript,
  // and a currency or a price change in the admin needs no edit here.
  const writeText = (id, text) => {
    const el = document.getElementById(id);
    if (el && text) el.textContent = text;
  };

  const syncCopy = (radio) => {
    const d = radio.dataset;
    const anchored = d.anchor === '1';
    const was = document.getElementById('d2-price-was');
    const save = document.getElementById('d2-price-save');
    writeText('d2-price-now', d.price);
    if (was) {
      if (d.was) was.textContent = d.was;
      was.hidden = !anchored;
    }
    if (save) {
      if (d.save) save.textContent = d.save;
      save.hidden = !anchored;
    }
    writeText('d2-buy-cta', d.cta);
    writeText('d2-close-cta', d.cta);
    writeText('d2-bar-price', d.bar);
    writeText('d2-bar-lift', d.liftlow);
    writeText('d2-incl-lift', d.lift);
    writeText('d2-welcome-off', d.welcome);

    // Shop Pay's monthly figure. The element Shopify emits re-renders itself
    // when variant-id changes, which is the only way to move it: the filter
    // reads the form's variant server-side and ignores any input we add.
    document.querySelectorAll('.d2-terms shopify-payment-terms').forEach((terms) => {
      if (radio.value) terms.setAttribute('variant-id', radio.value);
    });
  };

  // The address bar follows the choice so a share, a refresh or a back button
  // lands on the same lift and shade. replaceState, never pushState: two taps
  // on a swatch must not put two entries in the history a buyer has to press
  // back through. The Liquid reads ?variant= on the way in, so a reload of
  // this URL paints the right row server-side.
  const writeVariantUrl = (value) => {
    if (!value || !window.history || typeof history.replaceState !== 'function') return;
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', value);
      history.replaceState(history.state, '', url.toString());
    } catch (error) {
      /* an exotic URL is not worth breaking the picker over */
    }
  };

  const paintDrag = () => {
    if (!drag) return;

    if (!isScroller()) {
      // hand the drag back to the stylesheet's :checked steps
      drag.style.transform = '';
      drag.style.transition = '';
      return;
    }

    const span = stack.scrollWidth - stack.clientWidth;
    const steps = frames.length - 1;
    const position = span > 0 ? (stack.scrollLeft / span) * steps : 0;

    // no transition while it is following a finger; the scroll IS the animation
    drag.style.transition = 'none';
    drag.style.transform = 'translateX(' + position * 100 + '%)';

    // keep the radios telling the truth about where the row is, so the
    // scrollbar's focus ring and the colour map both stay coherent. Assigning
    // .checked fires no change event, so this cannot loop back into a scroll.
    const nearest = document.getElementById('d2-gal-' + (Math.round(position) + 1));
    if (nearest && !nearest.checked) nearest.checked = true;
  };

  // Whether the visitor has taken hold of the row themselves. Until they have,
  // startup is allowed to park it; after, it must not be moved under them.
  let touched = false;

  if (stack) {
    let painting = false;
    stack.addEventListener(
      'scroll',
      () => {
        if (painting) return;
        painting = true;
        requestAnimationFrame(() => {
          painting = false;
          paintDrag();
        });
      },
      { passive: true }
    );
    ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach((type) => {
      stack.addEventListener(type, () => { touched = true; }, { passive: true });
    });
  }

  /* A fresh visit opens on frame 1 with the black card selected, and that is
     correct. A RETURN visit is not: Chrome restores a scroll container's
     position, so someone who swiped to the grey pillow, tapped through to a
     review photograph and pressed back came back to the grey photograph with
     the BLACK card still ticked — the picture and the buy box disagreeing about
     what they are looking at.

     So startup parks the row on whichever frame the gallery radio says is live,
     without smoothing, and only while the visitor has not already grabbed it.
     Both hooks run: DOMContentLoaded catches the common case, load catches a
     restoration that lands after the images finish. */
  const parkOnCheckedFrame = () => {
    if (touched) return;
    const live = document.querySelector('input[name="d2-gal"]:checked');
    if (!live) return;
    const index = frameIndexOf(live.id);
    if (Number.isNaN(index)) return;
    goToFrame(index, false);
    paintDrag();
  };

  // ---- colour and segment taps, both through goToFrame ---------------------

  document.addEventListener('change', (event) => {
    const picked = event.target;
    if (!(picked instanceof HTMLInputElement) || !picked.checked) return;

    // a gallery radio moved: the scrollbar segments are labels for these, so
    // this is where a segment tap becomes a scroll
    if (picked.name === 'd2-gal') {
      goToFrame(frameIndexOf(picked.id), true);
      return;
    }

    // Only the buy panel's own picker gets to speak for the cart. The class is
    // the guard rather than name="id", which every product form on the page
    // uses — including the closing one this block writes into. And only a
    // radio: the panel's compare toggle (2026-09-21) is a checkbox in the
    // same form, and a checkbox's value is "on" — one of those reached the
    // closing form's variant id before this line existed.
    if (!picked.classList.contains('d2-pick__in') || picked.type !== 'radio') return;

    // mirror the live variant into the closing button nine screens down, and
    // into the buy panel's own ref="variantId" input (2026-09-07 — that input
    // exists so ProductFormComponent can finish connecting; it carries no name
    // and sits outside the form, so it posts nothing. Kept truthful anyway:
    // a field named after the variant must not hold a colour nobody picked).
    const closeId = document.getElementById('d2-close-id');
    if (closeId && picked.value) closeId.value = picked.value;

    const variantRef = document.getElementById('d2-variant-ref');
    if (variantRef && picked.value) variantRef.value = picked.value;

    syncCopy(picked);
    writeVariantUrl(picked.value);
    applyPick(picked, true);

    // 2026-09-22: the sticky bar's sheet (sections/d2-sticky.liquid) is a
    // checkbox-driven panel of <label for> these radios; rhode's closes on a
    // pick, so a pick unchecks the box. Escape below does the same.
    const sheet = document.getElementById('d2-bar-open');
    if (sheet) sheet.checked = false;
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const sheet = document.getElementById('d2-bar-open');
    if (sheet && sheet.checked) sheet.checked = false;
  });

  // 5. THE URL'S VARIANT (2026-08-28). The home page's grey shade card lands
  //    here as /products/…?variant=<grey id>, but the picker's markup opens on
  //    black regardless — the checked attribute is written in the Liquid, so
  //    the page and the address bar disagreed about what was being bought.
  //    Read the query once and write the same three states a finger's change
  //    event would: the picker radio, the closing button's mirror, the gallery
  //    frame. NOT via dispatching change — the change handler answers with a
  //    SMOOTH scroll, and at script-eval time that races the load: paintDrag
  //    reads the row still sitting at 0 and "corrects" the frame radio back to
  //    1 before the animation has moved anything. Setting the frame radio and
  //    leaving the movement to parkOnCheckedFrame (which positions instantly
  //    on DOMContentLoaded/load, and exists for exactly this parking job)
  //    cannot lose that race. An unknown or missing id falls through to the
  //    markup's own default — the failure mode is Black, never an empty
  //    selection.
  const urlVariant = new URLSearchParams(window.location.search).get('variant');
  if (urlVariant) {
    const fromUrl = Array.from(document.querySelectorAll('input.d2-pick__in')).find(
      (input) => input.value === urlVariant && !input.disabled
    );
    if (fromUrl && !fromUrl.checked) {
      fromUrl.checked = true;

      const closeMirror = document.getElementById('d2-close-id');
      if (closeMirror) closeMirror.value = fromUrl.value;

      const variantRefMirror = document.getElementById('d2-variant-ref');
      if (variantRefMirror) variantRefMirror.value = fromUrl.value;

      // instantly, not smoothly: paintDrag treats the scroll position as the
      // truth about which frame is live (measureScrollInset calls it three
      // lines below), and a smooth scroll started here loses that race. Frame
      // 1 is where applyPick parks anyway, so on a fresh load this is a
      // no-op on the row and only the two gallery classes actually change.
      syncCopy(fromUrl);
      applyPick(fromUrl, false);
    }
  }

  // 2026-09-22: the markup can now open on any of the four variants (the
  // Liquid reads product.selected_variant), so the gallery has to be told
  // which one that was even when no correction is needed above.
  {
    const opening = document.querySelector('input.d2-pick__in:checked');
    // park on the packshot only when the address named a variant
    if (opening) applyPick(opening, false, Boolean(urlVariant));
  }

  let queued = false;
  const measureScrollInset = () => {
    queued = false;
    const wrapper = document.querySelector('.page-wrapper');
    const inset = wrapper ? Math.max(0, window.innerWidth - wrapper.clientWidth) : 0;
    document.documentElement.style.setProperty('--d2-scroll-inset', inset + 'px');
    // same lifecycle: crossing 990 in either direction has to either take the
    // drag over or hand it back, and on the way in the row may already be
    // scrolled (a reload restores the position before this file runs).
    paintDrag();
  };
  // rAF only to coalesce resize; the first measurement is synchronous because a
  // frame never arrives in a background tab, and the bar would then sit wrong
  // for anyone who opened the page in one.
  const queueMeasure = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(measureScrollInset);
  };

  measureScrollInset();
  document.addEventListener('DOMContentLoaded', () => {
    measureScrollInset();
    parkOnCheckedFrame();
  });
  window.addEventListener('load', () => {
    measureScrollInset();
    parkOnCheckedFrame();
  });
  window.addEventListener('resize', queueMeasure);

  // 3. THE REVIEW WALL. sections/d2-reviews.liquid puts the whole corpus in the
  //    page and shows the first six; SHOW MORE uncovers ten more at a time.
  //
  //    2026-09-07: the histogram rows are buttons now (全店体检 cs-004 — the
  //    bars advertised three one-star reviews that no reader could reach), so
  //    one state machine drives both controls. `star` is the rating being
  //    filtered, 0 for the whole wall, and shownBy remembers how far down each
  //    of those six views the reader had got — leave 5★ forty reviews deep,
  //    look at the 1★ ones, come back, and you are still forty deep.
  //
  //    Only ever toggles .is-hidden, which is what the photo viewer already
  //    reads to know which thumbnails its arrows may walk.
  //
  //    Plain document-level listener on purpose — Horizon's component
  //    delegation rewrites event.target for on:click handlers and this must
  //    not go through it.
  const wall = document.getElementById('d2-reviews');
  if (wall) {
    const REVEAL = 10;
    const START = 6;

    const cards = Array.from(wall.querySelectorAll('.review'));
    const bars = Array.from(wall.querySelectorAll('[data-rv-star]'));
    const moreBox = wall.querySelector('.rev-more');
    const status = wall.querySelector('[data-rv-status]');
    const statusText = wall.querySelector('[data-rv-status-text]');

    let star = 0;
    const shownBy = { 0: START };

    const matching = () =>
      star === 0 ? cards : cards.filter((card) => card.dataset.rvRating === String(star));

    const apply = () => {
      const set = matching();
      const shown = Math.min(shownBy[star], set.length);
      const open = new Set(set.slice(0, shown));

      for (const card of cards) card.classList.toggle('is-hidden', !open.has(card));
      if (moreBox) moreBox.hidden = shown >= set.length;

      for (const bar of bars) {
        const on = bar.dataset.rvStar === String(star);
        bar.setAttribute('aria-pressed', String(on));
        bar.classList.toggle('is-on', on);
      }

      if (status) {
        status.hidden = star === 0;
        // Only write the line while it is on screen — an unfiltered wall would
        // otherwise be carrying "6 of 412 · 0 stars" in the DOM for anything
        // that reads text without honouring [hidden].
        if (statusText) {
          statusText.textContent =
            star === 0
              ? ''
              : shown + ' of ' + set.length + ' \u00b7 ' + star + (star === 1 ? ' star' : ' stars');
        }
      }
    };

    const pick = (next) => {
      star = next;
      if (!(star in shownBy)) shownBy[star] = START;
      apply();
    };

    document.addEventListener('click', (event) => {
      const node = event.target instanceof Element ? event.target : null;
      if (!node || !wall.contains(node)) return;

      const bar = node.closest('[data-rv-star]');
      if (bar) {
        const picked = Number(bar.dataset.rvStar);
        pick(star === picked ? 0 : picked);
        return;
      }

      if (node.closest('[data-rv-clear]')) {
        pick(0);
        return;
      }

      if (node.closest('[data-rev-more]')) {
        shownBy[star] = Math.min(shownBy[star] + REVEAL, matching().length);
        apply();
      }
    });
  }

  // 4. THE REVIEW PHOTO VIEWER. Tapping a thumbnail used to open the raw CDN
  //    file in a new tab, which on a phone means leaving the shop to look at a
  //    JPEG. It opens a dialog now with the photo and the review beside it.
  //
  //    The panel is filled by CLONING the nodes out of the card that was
  //    tapped — the stars, the title, the body and the verified chip are all
  //    already rendered on the wall, so nothing is duplicated in the markup and
  //    no review text is ever re-parsed from a data- attribute.
  //
  //    The arrows walk whatever photos are on the wall at that moment. SHOW
  //    MORE grows that list, so the set is read fresh on every open rather than
  //    cached at load.
  const lightbox = document.getElementById('rev-lightbox');
  if (lightbox) {
    const img = lightbox.querySelector('[data-rev-lb-img]');
    const who = lightbox.querySelector('[data-rev-lb-who]');
    const stars = lightbox.querySelector('[data-rev-lb-stars]');
    const title = lightbox.querySelector('[data-rev-lb-title]');
    const text = lightbox.querySelector('[data-rev-lb-text]');
    const count = lightbox.querySelector('[data-rev-lb-count]');
    const prev = lightbox.querySelector('[data-rev-lb-prev]');
    const next = lightbox.querySelector('[data-rev-lb-next]');

    let shots = [];
    let at = 0;

    const visibleShots = () =>
      Array.from(document.querySelectorAll('#d2-reviews [data-rev-photo]')).filter(
        (node) => !node.closest('.review.is-hidden')
      );

    const put = (target, source) => {
      target.replaceChildren();
      if (source) target.append(source.cloneNode(true));
    };

    const show = (index) => {
      const button = shots[index];
      if (!button) return;
      at = index;

      const card = button.closest('.review');
      const thumb = button.querySelector('img');

      // 2026-09-02. The markup reserves the big photo at 1400x1400, so a 3:2
      // file spent its whole load (0.9-1.1s) behind a grey SQUARE that then
      // snapped to 3:2. The thumbnail that was just tapped has already
      // rendered, so its real proportions are known here: hand them to the
      // big <img> before the src goes in and the stage reserves the right
      // shape from the first frame. Falls through to the markup's square only
      // if the thumb somehow has no size yet.
      if (thumb && thumb.naturalWidth && thumb.naturalHeight) {
        img.width = thumb.naturalWidth;
        img.height = thumb.naturalHeight;
      }

      img.src = button.dataset.full || (thumb && thumb.src) || '';
      img.alt = (thumb && thumb.alt) || '';

      who.replaceChildren();
      const name = card && card.querySelector('.meta .name');
      const chip = card && card.querySelector('.meta .vb');
      if (name) who.append(name.cloneNode(true));
      if (chip) who.append(chip.cloneNode(true));

      put(stars, card && card.querySelector('.rating'));
      put(title, card && card.querySelector('.title'));
      put(text, card && card.querySelector('.text'));

      count.textContent = shots.length > 1 ? `${index + 1} / ${shots.length}` : '';
      prev.hidden = shots.length < 2;
      next.hidden = shots.length < 2;
    };

    const step = (delta) => {
      if (shots.length < 2) return;
      show((at + delta + shots.length) % shots.length);
    };

    // Dropping the src keeps a shut viewer from holding a 1400px photo. It is
    // wired to every exit rather than to the 'close' event alone: the two
    // button paths call this directly, and cancel/close cover Escape and any
    // programmatic close. release() is idempotent, so overlapping paths are
    // fine.
    const release = () => img.removeAttribute('src');
    const dismiss = () => {
      if (lightbox.open) lightbox.close();
      release();
    };

    document.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      if (target.closest('[data-rev-lb-close]')) {
        dismiss();
        return;
      }
      if (target.closest('[data-rev-lb-prev]')) {
        step(-1);
        return;
      }
      if (target.closest('[data-rev-lb-next]')) {
        step(1);
        return;
      }

      const opener = target.closest('[data-rev-photo]');
      if (!opener) return;

      shots = visibleShots();
      const index = shots.indexOf(opener);
      if (index < 0) return;

      show(index);
      if (!lightbox.open) lightbox.showModal();
    });

    // Click the backdrop to dismiss. A click whose target is the dialog itself
    // landed on the backdrop, not on the photo or the panel.
    lightbox.addEventListener('click', (event) => {
      if (event.target === lightbox) dismiss();
    });

    lightbox.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') step(-1);
      if (event.key === 'ArrowRight') step(1);
    });

    lightbox.addEventListener('cancel', release);
    lightbox.addEventListener('close', release);
  }
  // 6. THE STICKY BAR'S BUTTON (2026-09-08). It shipped as an <a href="#d2-offer">
  //    — a scroll back to the picker, on the 2026-08-23 reasoning that a reader
  //    four screens down has not chosen a colour yet and an add here would
  //    choose for them.
  //
  //    On the desktop that reasoning never held. The buy panel sits in the
  //    FIRST screen beside the gallery (measured on live: #d2-offer top 127px,
  //    inViewport true at scrollY 0), so the anchor pointed at something
  //    already on screen and the page did not move — a control that looks
  //    pressable and answers with nothing. Leo, 2026-09-08, on the desktop
  //    bar: "为什么还是就是点击不了".
  //
  //    It now presses the picker's own add button. That posts whichever colour
  //    is live — the default the markup opens on, or Dove Grey if they picked
  //    it — and inherits the whole existing path for free: the AJAX post, the
  //    gift ebook line, the drawer opening. Nothing about the cart is repeated
  //    here.
  //
  //    The <a> stays in the markup rather than becoming a <button>: with this
  //    file absent the anchor still carries a reader down to the picker, which
  //    is the old behaviour rather than a dead control. Same reason the handler
  //    returns without preventDefault when the panel is missing or sold out.
  //
  //    Plain document-level listener, like the review wall above: Horizon's
  //    component delegation rewrites event.target for its own on:click
  //    handlers and this must not go through it.
  document.addEventListener('click', (event) => {
    const node = event.target instanceof Element ? event.target : null;
    const pill = node && node.closest('.sticky-bar .pill-btn');
    if (!pill) return;

    const panel = document.getElementById('d2-buy');
    const add = panel && panel.querySelector('button[type="submit"], input[type="submit"]');
    if (!add || add.disabled) return;

    event.preventDefault();
    add.click();
  });

  // 7. THE BAR STEPS ASIDE FOR THE BUY PANEL (2026-09-22). On the desktop the
  //    buy panel is in the first screen and its colour swatches land under the
  //    bar (1280x900). This only reports whether the panel is on screen, as a
  //    class on <html>; sections/d2-sticky.liquid decides what that means, and
  //    it only means anything above 767 — the phone bar stays put.
  const offerPanel = document.getElementById('d2-offer');
  if (offerPanel && 'IntersectionObserver' in window) {
    new IntersectionObserver((entries) => {
      document.documentElement.classList.toggle('d2-offer-in-view', entries[0].isIntersecting);
    }).observe(offerPanel);
  }

  // 7b. THE PHONE BAR WAITS FOR THE BUY BUTTON (2026-09-28, motion board item
  //    4). Rhode's rule: the bar rises once the panel's own add button has
  //    scrolled off the TOP of the screen, and sinks again when the button
  //    comes back or the page reaches its footer. This sets d2-bar-up on
  //    <html> for exactly that state; sections/d2-sticky.liquid only reads it
  //    below 768 (the desktop keeps rule 7). If anything needed here is
  //    missing, the bar is simply left up, never stranded below the screen.
  //
  //    Read from positions on every scroll frame, not from an
  //    IntersectionObserver: a jump straight from the footer to the top (a tap
  //    on the iPhone status bar) takes the button from above the screen to
  //    below it without ever intersecting, the observer stays silent, and the
  //    bar was left up over the first screen.
  {
    const root = document.documentElement;
    const bar = document.querySelector('.sticky-bar');
    const buyPanel = document.getElementById('d2-buy');
    const buyButton = buyPanel && buyPanel.querySelector('button[type="submit"], input[type="submit"]');
    const footer = document.querySelector('.d2-footer');
    if (bar && buyButton) {
      let queued = false;
      const paint = () => {
        queued = false;
        const passed = buyButton.getBoundingClientRect().bottom <= 0;
        const atFoot = !!footer && footer.getBoundingClientRect().top < window.innerHeight;
        root.classList.toggle('d2-bar-up', passed && !atFoot);
      };
      const ask = () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(paint);
      };
      // capture: the desktop scrolls .page-wrapper, not the window
      document.addEventListener('scroll', ask, { passive: true, capture: true });
      window.addEventListener('resize', ask, { passive: true });
      window.addEventListener('load', ask);
      paint();
    } else if (bar) {
      root.classList.add('d2-bar-up');
    }
  }

  // 8. KEYBOARD FOCUS IN THE PICKER COMES ON SCREEN (2026-09-27). The picker's
  //    four radios and its compare checkbox are position:fixed (the "FIXED,
  //    not absolute" note in snippets/d2-offer-body.liquid): every control on
  //    the panel is a <label for>, a tap hands focus to the input, and while
  //    the inputs sat at the top of the form the browser scrolled to them —
  //    a swatch tap threw the panel 339px up the page at 390. A fixed element
  //    is always on screen, so that focus scrolls nothing.
  //
  //    It scrolled nothing for the keyboard too, and there it was wrong. At
  //    390 the lift cards start below the fold (the deep lift card at
  //    951-1077 in an 844 viewport), so Tab into the group drew the card's
  //    ring where nobody could see it, and one Tab later the compare link's
  //    the same way (cold check, 2026-09-27).
  //
  //    So when one of these inputs takes focus FROM THE KEYBOARD — the same
  //    :focus-visible the rings are drawn on, false for a tap or a click — the
  //    thing wearing the ring is brought on screen: the lift card for a radio,
  //    the label for the checkbox. Only when it is not already clear of the
  //    pinned header and the sticky bar, and to the middle of the screen, the
  //    way the browser places a focused control of its own. A browser without
  //    :focus-visible keeps the old behaviour: nothing moves.
  const ringOf = (input) => {
    const row = input.dataset.ver && input.form && input.form.querySelector('.ver-row--' + input.dataset.ver);
    if (row) return row;
    // no lift rows, or the checkbox: the first label for it that is drawn
    const labels = input.id ? document.querySelectorAll('label[for="' + input.id + '"]') : [];
    return Array.from(labels).find((label) => label.getClientRects().length > 0) || null;
  };

  document.addEventListener('focusin', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('.d2-pick__in, .d2-cmp__in')) return;

    let keyboard = false;
    try {
      keyboard = input.matches(':focus-visible');
    } catch (error) {
      /* no :focus-visible in this browser */
    }
    if (!keyboard) return;

    const ring = ringOf(input);
    if (!ring) return;

    // Clear = below the pinned header (the scroll-padding duet-tokens.css
    // keeps for in-page anchors) and above the bar while the bar is up.
    const clearTop = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
    let clearBottom = window.innerHeight;
    const bar = document.querySelector('.sticky-bar');
    if (bar && getComputedStyle(bar).visibility !== 'hidden') {
      const barBox = bar.getBoundingClientRect();
      if (barBox.height > 0 && barBox.top < clearBottom) clearBottom = barBox.top;
    }

    const box = ring.getBoundingClientRect();
    if (box.top >= clearTop && box.bottom <= clearBottom) return;
    ring.scrollIntoView({ block: 'center', inline: 'nearest' });
  });
})();
